import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { ProjectLayout } from "@/components/layout/ProjectLayout";
import { OrgLayout } from "@/components/layout/OrgLayout";
import { OrgSettingsLayout } from "@/pages/OrgSettings";
import { OrgOverview } from "@/pages/OrgSettings/OrgOverview";
import { RequireAuth, RequireSuperadmin } from "@/lib/auth";
import { Login } from "@/pages/Login";
import { Setup } from "@/pages/Setup";
import { Dashboard } from "@/pages/Dashboard";
import { Deployments } from "@/pages/Deployments";
import { DeploymentDetail } from "@/pages/Deployment";
import { AccountLayout } from "@/pages/Account";
import { AccountProfile } from "@/pages/AccountProfile";
import { AccountPreferences } from "@/pages/AccountPreferences";
import { AccountTokens } from "@/pages/AccountTokens";
import { ProjectWebhooks } from "@/pages/ProjectWebhooks";
import { LogDrains } from "@/pages/LogDrains";
import { EnvGroups } from "@/pages/OrgSettings/EnvGroups";
import { GitHubToken } from "@/pages/OrgSettings/GitHubToken";
import { ProjectSettings } from "@/pages/ProjectSettings";
import { Environments } from "@/pages/Environments";
import { Import } from "@/pages/Import";
import { OrgDomains } from "@/pages/Domains";
import { ProjectDomains } from "@/pages/ProjectDomains";
import { ProjectOverview } from "@/pages/ProjectOverview";
import { ProjectProtection } from "@/pages/ProjectProtection";
import { ProjectActivity } from "@/pages/ProjectActivity";
import { AdminLayout } from "@/pages/Admin";
import { AdminDomains } from "@/pages/Admin/AdminDomains";
import { AdminEmail } from "@/pages/Admin/AdminEmail";
import { Previews } from "@/pages/Previews";
import { OrgMembers } from "@/pages/OrgMembers";
import { AuditLog } from "@/pages/AuditLog";
import { InviteAccept } from "@/pages/InviteAccept";
import { ProjectMembers } from "@/pages/ProjectMembers";
import { Trash } from "@/pages/Trash";
import { DeployHooks } from "@/pages/DeployHooks";
import { Analytics } from "@/pages/Analytics";
import { CronJobs } from "@/pages/CronJobs";
import { RouteRules } from "@/pages/RouteRules";
import { FeatureFlags } from "@/pages/FeatureFlags";
import { NotFound } from "@/pages/NotFound";

export function App() {
  return (
    <Routes>
      {/* Standalone (no shell) */}
      <Route path="/setup" element={<Setup />} />
      <Route path="/login" element={<Login />} />
      <Route path="/invite/:token" element={<InviteAccept />} />

      {/* Authenticated shell */}
      <Route element={<RequireAuth><AppShell /></RequireAuth>}>
        <Route index element={<Dashboard />} />
        <Route path="/orgs/new/import" element={<Import />} />
        <Route path="/admin" element={<RequireSuperadmin><AdminLayout /></RequireSuperadmin>}>
          <Route index element={<AdminDomains />} />
          <Route path="email" element={<AdminEmail />} />
        </Route>
        <Route path="/account" element={<AccountLayout />}>
          <Route index element={<AccountProfile />} />
          <Route path="tokens" element={<AccountTokens />} />
          <Route path="preferences" element={<AccountPreferences />} />
        </Route>

        <Route path="/orgs/:org" element={<OrgLayout />}>
          <Route index element={<Dashboard />} />
          <Route path="settings" element={<OrgSettingsLayout />}>
            <Route index element={<OrgOverview />} />
            <Route path="members" element={<OrgMembers />} />
            <Route path="domains" element={<OrgDomains />} />
            <Route path="env-groups" element={<EnvGroups />} />
            <Route path="github" element={<GitHubToken />} />
            <Route path="audit" element={<AuditLog />} />
          </Route>
          <Route path="trash" element={<Trash />} />
        </Route>

        <Route path="/orgs/:org/projects/:project" element={<ProjectLayout />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<ProjectOverview />} />
          <Route path="deployments" element={<Deployments />} />
          <Route path="deployments/:id" element={<DeploymentDetail />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="rules" element={<RouteRules />} />
          <Route path="cron" element={<CronJobs />} />
          <Route path="flags" element={<FeatureFlags />} />
          <Route path="previews" element={<Previews />} />
          <Route path="domains" element={<ProjectDomains />} />
          <Route path="env" element={<Environments />} />
          <Route path="webhooks" element={<ProjectWebhooks />} />
          <Route path="log-drains" element={<LogDrains />} />
          <Route path="deploy-hooks" element={<DeployHooks />} />
          <Route path="settings" element={<ProjectSettings />} />
          <Route path="settings/members" element={<ProjectMembers />} />
          <Route path="protection" element={<ProjectProtection />} />
          <Route path="activity" element={<ProjectActivity />} />
        </Route>
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
